import "reflect-metadata";
import {TransportServices} from "../transport-services";
import {PipelineStatistics, PipelineStatisticsWrapper} from "./pipeline-statistics";
import {TruckingStatistics, TruckingStatisticsWrapper} from "./trucking-statistics";
import {InventoryStatistics, InventoryStatisticsWrapper} from "./inventory-statistics";
import {Exclude, Expose} from "class-transformer";
import {transportMethod, TransportMethod} from "../transport-method";
import {statisticsType, StatisticsType} from "./statistics";
import {TransportCategory} from "../transport-category";
import {calculateAverage} from "../../util/math-util";
import {round} from "mathjs";

export interface StatisticsGrouping {
  trucking: TruckingStatistics
  pipeline: PipelineStatistics
  inventory: InventoryStatistics
  transportServicesList: TransportServices[]
}

@Exclude()
export class StatisticsGroupingWrapper implements StatisticsGrouping {
  constructor() {
    if (this.constructor === StatisticsGroupingWrapper) {
      throw new Error("Can't instantiate abstract class");
    }
  }

  // trucking
  @Expose()
  trucking = new TruckingStatisticsWrapper();

  // pipeline
  @Expose()
  pipeline = new PipelineStatisticsWrapper();

  // inventory
  @Expose()
  inventory = new InventoryStatisticsWrapper()

  // data
  @Expose()
  transportServicesList: TransportServices[] = [];

  processedTransportIds = new Set<string>()

  getValue = (
    method: TransportMethod | undefined = undefined,
    type: StatisticsType,
    rateModel: string | undefined = undefined,
    category: TransportCategory | undefined = undefined
  ): number => {
    let value = 0;
    if (method === transportMethod.pipeline) {
      value = this.pipeline.getValue(type, rateModel ?? 'Pipeline', category);
    } else if (method === transportMethod.trucking) {
      value = this.trucking.getValue(type, rateModel ?? "Hauling", category);
    } else {
      switch (type) {
        case statisticsType.cost:
          value = this.getTotalCost(rateModel, category)
          break;
        case statisticsType.billableTime:
          value = this.getBillableHours(rateModel, category)
          break;
        case statisticsType.billableQuantity:
          value = this.getBillableQty(rateModel, category)
          break;
        case statisticsType.timeCost:
          value = this.getTimeCost(rateModel, category)
          break;
        case statisticsType.genericTimeCost:
          value = this.getGenericTimeCost(rateModel, category)
          break;
        case statisticsType.siteRestrictionTimeCost:
          value = this.getSiteRestrictionTimeCost(rateModel, category)
          break;
        case statisticsType.demurrageTimeCost:
          value = this.getDemurrageTimeCost(rateModel, category)
          break;
        case statisticsType.pickupDemurrageTimeCost:
          value = this.getPickupDemurrageTimeCost(rateModel, category)
          break;
        case statisticsType.deliveryDemurrageTimeCost:
          value = this.getDeliveryDemurrageTimeCost(rateModel, category)
          break;
        case statisticsType.freightCost:
          value = this.getFreightCost(rateModel, category)
          break;
        case statisticsType.genericFreightCost:
          value = this.getGenericFreightCost(rateModel, category)
          break;
        case statisticsType.siteRestrictionFreightCost:
          value = this.getSiteRestrictionFreightCost(rateModel, category)
          break;
        case statisticsType.loadCost:
          value = this.getLoadCost(rateModel, category)
          break;
        case statisticsType.genericLoadCost:
          value = this.getGenericLoadCost(rateModel, category)
          break;
        case statisticsType.siteRestrictionLoadCost:
          value = this.getSiteRestrictionLoadCost(rateModel, category)
          break;
        case statisticsType.baseFuelSurchargeLoadCost:
          value = this.getBaseFuelSurchargeLoadCost(rateModel, category)
          break;
        case statisticsType.percentFuelSurchargeLoadCost:
          value = this.getPercentFuelSurchargeLoadCost(rateModel, category)
          break;
        case statisticsType.shiftLoadCost:
          value = this.getShiftLoadCost(rateModel, category)
          break;
        case statisticsType.ticketRejectLoadCost:
          value = this.getTicketRejectLoadCost(rateModel, category)
          break;
        case statisticsType.splitLoadCost:
          value = this.getSplitLoadCost(rateModel, category)
          break;
        case statisticsType.taxCost:
          value = this.getTaxCost(rateModel, category)
          break;
        case statisticsType.siteRestrictionCost:
          value = this.getSiteRestrictionCost(rateModel, category)
          break;
        case statisticsType.fuelSurchargeCost:
          value = this.getFuelSurchargeCost(rateModel, category)
          break;
        case statisticsType.costPerQuantity: {
          const cost = this.getTotalCost(rateModel, category);
          const qty = this.getTotalQty(category);
          value = calculateAverage(cost, qty);
          break;
        }
        default: {
          value = this.getTotalValue(type, rateModel, category)
          break;
        }
      }
    }

    return value;
  };

  getTotalCost = (rateModel: string | undefined, category: TransportCategory | undefined = undefined): number => {
    return this.getTotalValue(statisticsType.cost, rateModel, category)
  };

  getBillableHours(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.billableTime, rateModel, category)
  }

  getBillableQty(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.billableQuantity, rateModel, category)
  }

  getTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.timeCost, rateModel, category)
  }

  getGenericTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.genericTimeCost, rateModel, category)
  }

  getSiteRestrictionTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.siteRestrictionTimeCost, rateModel, category)
  }

  getDemurrageTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.demurrageTimeCost, rateModel, category)
  }

  getPickupDemurrageTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.pickupDemurrageTimeCost, rateModel, category)
  }

  getDeliveryDemurrageTimeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.deliveryDemurrageTimeCost, rateModel, category)
  }

  getFreightCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.freightCost, rateModel, category)
  }

  getGenericFreightCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.genericFreightCost, rateModel, category)
  }

  getSiteRestrictionFreightCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.siteRestrictionFreightCost, rateModel, category)
  }

  getLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.loadCost, rateModel, category)
  }

  getGenericLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.genericLoadCost, rateModel, category)
  }

  getSiteRestrictionLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.siteRestrictionLoadCost, rateModel, category)
  }

  getBaseFuelSurchargeLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.baseFuelSurchargeLoadCost, rateModel, category)
  }

  getPercentFuelSurchargeLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.percentFuelSurchargeLoadCost, rateModel, category)
  }

  getShiftLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.shiftLoadCost, rateModel, category)
  }

  getTicketRejectLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.ticketRejectLoadCost, rateModel, category)
  }

  getSplitLoadCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.splitLoadCost, rateModel, category)
  }

  getTaxCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.taxCost, rateModel, category)
  }

  getSiteRestrictionCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.siteRestrictionCost, rateModel, category)
  }

  getFuelSurchargeCost(rateModel: string | undefined, category: TransportCategory | undefined = undefined): number {
    return this.getTotalValue(statisticsType.fuelSurchargeCost, rateModel, category)
  }

  getTotalQty = (category: TransportCategory | undefined = undefined): number => {
    return this.getTotalValue(statisticsType.quantity, undefined, category)
  };

  getTotalStandardQty = (category: TransportCategory | undefined = undefined): number => {
    return this.getTotalValue(statisticsType.standardQuantity, undefined, category)
  };

  getTotalNetQty = (category: TransportCategory | undefined = undefined): number => {
    return this.getTotalValue(statisticsType.netQuantity, undefined, category)
  };

  protected getTotalValue = (type: StatisticsType, rateModel: string | undefined, category: TransportCategory | undefined = undefined): number => {
    const truckingCost = this.trucking.getValue(type, rateModel ?? 'Hauling', category);
    const pipelineCost = this.pipeline.getValue(type, rateModel ?? 'Pipeline', category);
    return round(truckingCost + pipelineCost, 2);
  };
}
